/**
 * Chat WebSocket Service
 *
 * Manages Socket.io connection for real-time chat functionality:
 * - HTTP-only cookie authentication (no token parameter)
 * - Auto-reconnection
 * - Event handling
 * - Connection state management
 *
 * Uses conversationId-based architecture (not orderId)
 */

import { io, Socket } from 'socket.io-client';
import { API_CONFIG } from '../api/config';
import type {
  JoinConversationPayload,
  SendMessagePayload,
  TypingPayload,
  StopTypingPayload,
  MarkAsReadPayload,
  EditMessagePayload,
  MessageSentPayload,
  NewMessagePayload,
  ActorTypingPayload,
  ActorStoppedTypingPayload,
  MessagesReadPayload,
  ConversationUnreadUpdatedPayload,
  MessageEditedPayload,
  UserOnlinePayload,
  UserOfflinePayload,
  OfflineMessagesPayload,
  ErrorPayload,
  ConnectionStatus,
  Message,
  MessageType,
} from '@/src/widgets/chat/types';

type EventCallback<T> = (data: T) => void;

interface EventHandlers {
  // Connection events
  connect: EventCallback<void>;
  disconnect: EventCallback<void>;

  // Message events
  message_sent: EventCallback<MessageSentPayload>;
  new_message: EventCallback<NewMessagePayload>;
  message_edited: EventCallback<MessageEditedPayload>;

  // Typing events
  actor_typing: EventCallback<ActorTypingPayload>;
  actor_stopped_typing: EventCallback<ActorStoppedTypingPayload>;

  // Read receipts
  messages_read: EventCallback<MessagesReadPayload>;

  // Unread count updates
  conversation_unread_updated: EventCallback<ConversationUnreadUpdatedPayload>;

  // Online status
  user_online: EventCallback<UserOnlinePayload>;
  user_offline: EventCallback<UserOfflinePayload>;

  // Offline messages
  offline_messages: EventCallback<OfflineMessagesPayload>;

  // Errors
  error: EventCallback<ErrorPayload>;

  // Connection status
  connection_status: EventCallback<ConnectionStatus>;
}

export class ChatSocketService {
  private static instance: ChatSocketService;
  private socket: Socket | null = null;
  private connectionStatus: ConnectionStatus = 'connecting';
  private eventHandlers: Partial<EventHandlers> = {};
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;

  private constructor() {}

  static getInstance(): ChatSocketService {
    if (!ChatSocketService.instance) {
      ChatSocketService.instance = new ChatSocketService();
    }
    return ChatSocketService.instance;
  }

  /**
   * Connect to chat WebSocket server
   * Fetches token from HTTP-only cookie and passes it via auth parameter
   * Handles authentication failures gracefully
   */
  async connect(): Promise<void> {
    if (this.socket?.connected) {
      return;
    }

    // Set status to connecting immediately
    this.updateConnectionStatus('connecting');

    // Socket.IO uses HTTP for handshake, then upgrades to WebSocket
    const socketUrl = API_CONFIG.baseUrl;

    try {
      // Import fetchWithTokenRefresh dynamically to avoid circular dependencies
      const { fetchWithTokenRefresh } = await import('../api/refreshToken');

      // Fetch WebSocket token from server (reads from HTTP-only cookie)
      // Uses fetchWithTokenRefresh to automatically retry with fresh token on 401
      const response = await fetchWithTokenRefresh(`${socketUrl}/api/v1/auth/ws-token`, {
        credentials: 'include', // Include HTTP-only cookies
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('[ChatSocket] Not authenticated, cannot connect to WebSocket');
        } else {
          console.error('[ChatSocket] Failed to get WS token:', response.status);
        }
        return;
      }

      const { token } = await response.json();

      this.socket = io(`${socketUrl}/chat`, {
        auth: {
          token, // Pass token via auth parameter (supported by gateway)
        },
        withCredentials: true, // CRITICAL: Still include cookies for additional security
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: this.maxReconnectAttempts,
      });

      this.setupEventListeners();
    } catch (error) {
      console.error('[ChatSocket] Failed to connect:', error);
      this.updateConnectionStatus('disconnected');
    }
  }

  /**
   * Disconnect from server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.updateConnectionStatus('disconnected');
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get current connection status
   */
  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
      this.updateConnectionStatus('connected');
      this.emit('connect');
    });

    this.socket.on('disconnect', (reason) => {
      this.updateConnectionStatus('disconnected');
      this.emit('disconnect');

      if (reason === 'io server disconnect') {
        // Server disconnected us, need to reconnect manually
        this.socket?.connect();
      }
    });

    // Reconnection events
    this.socket.on('reconnect_attempt', (attempt) => {
      this.reconnectAttempts = attempt;
      this.updateConnectionStatus('connecting');
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('[ChatSocket] Reconnection error:', error);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[ChatSocket] Reconnection failed after max attempts');
      this.updateConnectionStatus('disconnected');
    });

    // Message events
    this.socket.on('message_sent', (data: MessageSentPayload) => {
      this.emit('message_sent', data);
    });

    this.socket.on('new_message', (data: NewMessagePayload) => {
      this.emit('new_message', data);
    });

    this.socket.on('message_edited', (data: MessageEditedPayload) => {
      this.emit('message_edited', data);
    });

    // Typing events (actor-based)
    this.socket.on('actor_typing', (data: ActorTypingPayload) => {
      this.emit('actor_typing', data);
    });

    this.socket.on('actor_stopped_typing', (data: ActorStoppedTypingPayload) => {
      this.emit('actor_stopped_typing', data);
    });

    // Read receipts
    this.socket.on('messages_read', (data: MessagesReadPayload) => {
      this.emit('messages_read', data);
    });

    // Unread count updates
    this.socket.on('conversation_unread_updated', (data: ConversationUnreadUpdatedPayload) => {
      this.emit('conversation_unread_updated', data);
    });

    // Online status
    this.socket.on('user_online', (data: UserOnlinePayload) => {
      this.emit('user_online', data);
    });

    this.socket.on('user_offline', (data: UserOfflinePayload) => {
      this.emit('user_offline', data);
    });

    // Offline messages
    this.socket.on('offline_messages', (data: OfflineMessagesPayload) => {
      this.emit('offline_messages', data);
    });

    // Errors
    this.socket.on('error', (data: ErrorPayload) => {
      console.error('[ChatSocket] Server error:', data);
      this.emit('error', data);
    });
  }

  /**
   * Update connection status and emit event
   */
  private updateConnectionStatus(status: ConnectionStatus): void {
    this.connectionStatus = status;
    this.emit('connection_status', status);
  }

  /**
   * Emit event to registered handlers
   */
  private emit<K extends keyof EventHandlers>(
    event: K,
    data?: Parameters<EventHandlers[K]>[0]
  ): void {
    const handler = this.eventHandlers[event];
    if (handler) {
      // @ts-expect-error - Type mismatch handled by runtime
      handler(data);
    }
  }

  /**
   * Register event handler
   */
  on<K extends keyof EventHandlers>(event: K, handler: EventHandlers[K]): void {
    this.eventHandlers[event] = handler;
  }

  /**
   * Unregister event handler
   */
  off<K extends keyof EventHandlers>(event: K): void {
    delete this.eventHandlers[event];
  }

  /**
   * Join conversation room
   */
  joinConversation(
    conversationId: string
  ): Promise<{ success: boolean; conversationId: string }> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      const payload: JoinConversationPayload = { conversationId };

      this.socket.emit('join_conversation', payload, (response: any) => {
        if (response?.success) {
          resolve(response);
        } else {
          reject(new Error(response?.message || 'Failed to join conversation'));
        }
      });
    });
  }

  /**
   * Send message to conversation
   */
  sendMessage(
    conversationId: string,
    body: string,
    type: MessageType = 'TEXT',
    metadata?: Record<string, unknown>,
    attachmentIds?: string[]
  ): Promise<Message> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      const payload: SendMessagePayload = {
        conversationId,
        body,
        type,
        metadata,
        attachmentIds,
      };

      this.socket.emit('send_message', payload, (response: any) => {
        if (response) {
          resolve(response);
        } else {
          reject(new Error('Failed to send message'));
        }
      });
    });
  }

  /**
   * Send typing indicator
   */
  typing(conversationId: string): void {
    if (!this.socket?.connected) return;

    const payload: TypingPayload = { conversationId };
    this.socket.emit('typing', payload);
  }

  /**
   * Send stop typing indicator
   */
  stopTyping(conversationId: string): void {
    if (!this.socket?.connected) return;

    const payload: StopTypingPayload = { conversationId };
    this.socket.emit('stop_typing', payload);
  }

  /**
   * Mark messages as read up to specified message
   */
  markAsRead(conversationId: string, lastReadMessageId: string): void {
    if (!this.socket?.connected) return;

    const payload: MarkAsReadPayload = { conversationId, lastReadMessageId };
    this.socket.emit('mark_as_read', payload);
  }

  /**
   * Edit message
   */
  editMessage(messageId: string, content: string): Promise<Message> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      const payload: EditMessagePayload = { messageId, content };

      this.socket.emit('edit_message', payload, (response: any) => {
        if (response) {
          resolve(response);
        } else {
          reject(new Error('Failed to edit message'));
        }
      });
    });
  }
}

// Export singleton instance
export const chatSocketService = ChatSocketService.getInstance();
